import { AxiosError } from "axios";

import api from "./apiClient";
import { SearchPaginationQuery, downloadResponseAsJSON } from ".";
import {
    Address,
    Carrier,
    DeliveryTask,
    DeliveryTaskStatus,
    HazardTask,
    HazardTaskType,
    ListResponse,
    LoadingTask,
    LoadingTaskEvent,
    LoadingTaskStatus,
    Store,
    TaskType,
    TourOrder,
    TourOrderDriverType,
    TourOrderStatus,
    Trace,
    Trailer,
    TrailerDepotTask,
    TrailerPickupTask,
    User,
    ValueListItem,
} from "./types";

export type TourOrderIdPayload = TourOrder["id"];
export type TourOrdersListPayload = SearchPaginationQuery & {
    from?: string;
    to?: string;
    status?: TourOrder["status"] | Array<TourOrder["status"]>;
    store?: Store["id"];
    carrier?: Carrier["id"];
    driver?: User["id"];
    activity?: ValueListItem["id"];
    isWithoutTrailer?: boolean;
    isLate?: boolean;
    isFresh?: boolean;
    isNonCompliance?: boolean;
    isWithoutDropZone?: boolean;
    atLeastOneHazard?: boolean;
    atLeastOneDeclinedDelivery?: boolean;
};

export enum TrailerType {
    temporary = "temporary",
    registered = "registered",
}

export type CreateTourOrderPayload = {
    status?: TourOrderStatus;
    createdBy: User["id"];
    date: string;
    driver: User["id"];
    driverType: TourOrderDriverType;
    truckRegistration: string;
    trailer?: {
        type: TrailerType;
        trailer?: Trailer["id"];
        registration?: string;
        location?: Trailer["location"];
    };
    scheduledDeparture: string;
    scheduledEndTime: string;
    expectedDistance: number;
    stores: Array<Store["id"]>;
    tasks: {
        loadingTasks: Array<{
            id?: LoadingTask["id"]; // only used for update
            index: number;
            initialStartTime: string;
            startTime: string;
            type: TaskType.loading;
            status: LoadingTaskStatus.todo;
            warehouse: ValueListItem["id"];
            numberOfPalletsToLoad: number;
            loadingItems: Array<{
                store: Store["id"];
                pallets: Array<{
                    product: ValueListItem["id"];
                    quantity: number;
                    orders: Array<string>;
                }>;
            }>;
            address: Address;
            events: Array<LoadingTaskEvent>;
        }>;
        deliveryTasks: Array<{
            id?: DeliveryTask["id"]; // only used for update
            index: number;
            type: TaskType.delivery;
            status: DeliveryTaskStatus.todo;
            initialStartTime: string;
            startTime: string;
            store: Store["id"];
            numberOfPallets: number;
            address: Address;
        }>;
    };
};

export type CreateTourOrderError = AxiosError<
    | {
          error: "trailerNotAvailable";
      }
    | undefined
>;

export interface TourOrderUpdatePayload extends Pick<
    CreateTourOrderPayload,
    | "driver"
    | "driverType"
    | "trailer"
    | "scheduledDeparture"
    | "scheduledEndTime"
    | "expectedDistance"
    | "stores"
    | "tasks"
    | "date"
> {
    id?: TourOrder["id"];
    updatedAt: string;
}

export type TourOrderUpdateError = AxiosError<
    | {
          error: "tourOrderOutdated";
      }
    | undefined
>;

export type ImportTourOrdersFromCsvError = AxiosError<
    | {
          error: "invalidFormat";
      }
    | undefined
>;

export enum AssignTrailerType {
    registered = "registered",
    temporary = "temporary",
}
export interface AssignTrailerPayload {
    tourOrderId: TourOrder["id"];
    type: AssignTrailerType;
    trailer?: Trailer["id"];
    registration?: Trailer["registration"];
    location?: ValueListItem["id"];
}
export interface UpdateTrailerLocationPayload {
    tourOrderId: TourOrder["id"];
    trailerPickupTaskId: TrailerPickupTask["id"];
    location: ValueListItem["id"];
}
export interface ChangeTruckPayload {
    tourOrderId: TourOrder["id"];
    truckRegistration: TourOrder["truckRegistration"];
}
export interface AddCommentPayload {
    tourOrderId: TourOrder["id"];
    operationComment?: string | null;
    shippingServiceComment?: string | null;
}
export interface AssignYardDriverPayload {
    tourOrderId: TourOrder["id"];
    taskId: string;
    yardDriver: User["id"] | null;
}

export interface AssignDriverPayload {
    tourOrderId: TourOrder["id"];
    userId: User["id"];
    driverType: TourOrderDriverType;
}

export interface ForcedTrailerPermissionPayload {
    tourOrderId: TourOrder["id"];
    trailerPickupTaskId: TrailerPickupTask["id"];
    accepted: boolean;
}

export interface LoadingForcedPlatformPermissionPayload {
    tourOrderId: TourOrder["id"];
    loadingTaskId: LoadingTask["id"];
    accepted: boolean;
    tourOrder?: TourOrder["id"];
    location?: ValueListItem["id"];
}

export interface TrailerDepotForcedPlatformPermissionPayload {
    tourOrderId: TourOrder["id"];
    trailerDepotTaskId: TrailerDepotTask["id"];
    accepted: boolean;
    tourOrder?: TourOrder["id"];
    location?: ValueListItem["id"];
}

export interface TrailerDepotForcedDepotZonePermissionPayload {
    tourOrderId: TourOrder["id"];
    trailerDepotTaskId: TrailerDepotTask["id"];
    accepted: boolean;
    location?: ValueListItem["id"];
}

export interface TracePayload {
    tourOrderId: TourOrder["id"];
    originalPoint?: boolean;
}

export interface AssignTrailerDepotLocationPayload {
    tourOrderId: TourOrder["id"];
    trailerDepotTaskId: TrailerDepotTask["id"];
    location?: ValueListItem["id"];
    tourOrder?: TourOrder["id"];
}

export interface CreateHazardTaskPayload {
    tourOrderId: TourOrder["id"];
    reason: ValueListItem["id"];
    hazardTaskType: HazardTaskType;
    delay?: number;
    users?: Array<User["id"]>;
    emails?: string[];
}

export interface UpdateHazardTaskPayload {
    tourOrderId: TourOrder["id"];
    hazardTaskId: HazardTask["id"];
    delay?: number;
    users?: Array<User["id"]>;
    emails?: string[];
}

export interface DeleteHazardTaskPayload {
    tourOrderId: TourOrder["id"];
    hazardTaskId: HazardTask["id"];
}
export interface ValidateUnloadedSupportsPayload {
    tourOrderId: TourOrder["id"];
    deliveryTasks: Array<{
        id: DeliveryTask["id"];
        returnSupports: Array<{
            support: string;
            returnedQuantity: number;
        }>;
    }>;
}

export interface SendDegradedModePayload {
    tourOrderId: TourOrder["id"];
    actualDeparture: string;
    trailerDepotStartTime: string;
    trailerDepotLocation: ValueListItem["id"];
    deliveryTasks: Array<{
        id: DeliveryTask["id"];
        startedTime: string;
        dockedAt: string;
        endedTime: string;
        status: DeliveryTaskStatus;
        supportsToDeliver?: Array<{
            support: string;
            quantity: number;
            deliveredQuantity: number;
        }>;
        returnSupports?: Array<{
            support: string;
            quantity: number;
            returnedQuantity: number;
        }>;
        nonCompliances?: Array<{
            reason: ValueListItem["id"];
        }>;
    }>;
}

export type ValidateUnloadedSupportsError = AxiosError<
    | {
          error: "tourOrderNotFound" | "invalidTourOrderStatus" | "deliveryTaskNotFound" | "supportNotFound";
      }
    | undefined
>;

export type TourOrdersListResponse = ListResponse<TourOrder>;
export type TourOrderDetailResponse = TourOrder;
export type AssignTrailerResponse = TourOrder;
export type UpdateTrailerLocationResponse = TourOrder;
export type AssignTrailerError = AxiosError<
    | {
          fields: {
              registration: Trailer["registration"];
          };
      }
    | undefined
>;

export type AssignTrailerDepotLocationError = AxiosError<
    | {
          trailerAlreadyAssigned?: boolean;
      }
    | undefined
>;

export enum HazardTaskDeclaration {
    during = "during",
    before = "before",
}

// Routes
export const create = async (payload: CreateTourOrderPayload) => {
    return await api.post<TourOrder>(`/tourOrders`, payload).then((response) => response?.data);
};

export const list = async (payload?: TourOrdersListPayload) => {
    return await api.get<TourOrdersListResponse>(`/tourOrders`, { params: payload }).then((response) => response?.data);
};

export const details = async (id?: TourOrderIdPayload) => {
    return await api.get<TourOrderDetailResponse>(`/tourOrders/${id}`).then((response) => response?.data);
};

export const update = async ({ id, ...payload }: TourOrderUpdatePayload) => {
    if (!id) {
        throw new Error("missing id");
    }
    return await api.put<TourOrder>(`/tourOrders/${id}`, payload).then((response) => response?.data);
};

export const assignTrailer = async ({ tourOrderId, ...payload }: AssignTrailerPayload) => {
    return await api
        .post<AssignTrailerResponse>(`/tourOrders/${tourOrderId}/trailer`, payload)
        .then((response) => response?.data);
};

export const updateTrailerLocation = async ({
    tourOrderId,
    trailerPickupTaskId,
    ...payload
}: UpdateTrailerLocationPayload) => {
    return await api
        .post<UpdateTrailerLocationResponse>(
            `/tourOrders/${tourOrderId}/trailerPickupTasks/${trailerPickupTaskId}/updateTrailerLocation`,
            payload,
        )
        .then((response) => response?.data);
};

export const changeTruck = async ({ tourOrderId, ...payload }: ChangeTruckPayload) => {
    return await api.post<TourOrder>(`/tourOrders/${tourOrderId}/truck`, payload).then((response) => response?.data);
};

export const addComment = async ({ tourOrderId, ...payload }: AddCommentPayload) => {
    return await api.post<TourOrder>(`/tourOrders/${tourOrderId}/comment`, payload).then((response) => response?.data);
};

export const cancelTourOrder = async (tourOrderId: TourOrderIdPayload) => {
    return await api.post<TourOrder>(`/tourOrders/${tourOrderId}/cancel`).then((response) => response?.data);
};

export const assignYardDriver = async ({ tourOrderId, taskId, ...payload }: AssignYardDriverPayload) => {
    return await api
        .post(`/tourOrders/${tourOrderId}/loadingTasks/${taskId}/yardDriver`, payload)
        .then((response) => response?.data);
};

export const assignDriver = async ({ tourOrderId, ...payload }: AssignDriverPayload) => {
    return await api.post(`/tourOrders/${tourOrderId}/assignDriver`, payload).then((response) => response?.data);
};

export const forcedTrailerPermission = async ({
    tourOrderId,
    trailerPickupTaskId,
    ...payload
}: ForcedTrailerPermissionPayload) => {
    return await api
        .post(`/tourOrders/${tourOrderId}/trailerPickupTasks/${trailerPickupTaskId}/forcedTrailerPermission`, payload)
        .then((response) => response.data);
};

export const loadingForcedPlatformPermission = async ({
    tourOrderId,
    loadingTaskId,
    ...payload
}: LoadingForcedPlatformPermissionPayload) => {
    return await api
        .post(`/tourOrders/${tourOrderId}/loadingTasks/${loadingTaskId}/forcedPlatformPermission`, payload)
        .then((response) => response.data);
};

export const trailerDepotforcedPlatformPermission = async ({
    tourOrderId,
    trailerDepotTaskId,
    ...payload
}: TrailerDepotForcedPlatformPermissionPayload) => {
    return await api
        .post(`/tourOrders/${tourOrderId}/trailerDepotTasks/${trailerDepotTaskId}/forcedDepotZonePermission`, payload)
        .then((response) => response.data);
};

export const trailerDepotforcedDepotZonePermission = async ({
    tourOrderId,
    trailerDepotTaskId,
    ...payload
}: TrailerDepotForcedDepotZonePermissionPayload) => {
    return await api
        .post(`/tourOrders/${tourOrderId}/trailerDepotTasks/${trailerDepotTaskId}/forcedDepotZonePermission`, payload)
        .then((response) => response.data);
};

export const trace = async ({ tourOrderId, originalPoint }: TracePayload) => {
    return await api
        .get<Trace>(`/tourOrders/${tourOrderId}/trace`, { params: { originalPoint } })
        .then((response) => response.data);
};

export interface UpdateDateOfTourOrderPayload {
    tourOrderId: TourOrder["id"];
    date?: string | null;
}
export const updateDateOfTourOrder = async ({ tourOrderId, ...payload }: UpdateDateOfTourOrderPayload) => {
    return await api
        .post(`/tourOrders/${tourOrderId}/updateDateOfTourOrder`, payload)
        .then((response) => response.data);
};

// Super admin routes
export const downloadTourOrderBody = async (tourOrderId: TourOrderIdPayload) => {
    return await api
        .get<Blob>(`/tourOrders/${tourOrderId}/downloadBody`, {
            responseType: "blob",
        })
        .then((response) => {
            downloadResponseAsJSON(response);
            return null;
        });
};

export const deleteTourOrder = async (tourOrderId: TourOrderIdPayload) => {
    return await api.delete(`/tourOrders/${tourOrderId}`).then((response) => response.data);
};

export const sendUpdateToDriver = async (tourOrderId: TourOrderIdPayload) => {
    return await api.post(`/tourOrders/${tourOrderId}/sendUpdateToDriver`).then((response) => response.data);
};

export const assignTrailerDepotLocation = async ({
    tourOrderId,
    trailerDepotTaskId,
    ...payload
}: AssignTrailerDepotLocationPayload) => {
    return await api
        .post(`/tourOrders/${tourOrderId}/trailerDepotTasks/${trailerDepotTaskId}/assignLocation`, payload)
        .then((response) => response.data);
};

export const createHazardTask = async ({ tourOrderId, ...payload }: CreateHazardTaskPayload) => {
    return await api.post(`/tourOrders/${tourOrderId}/hazardTask`, payload).then((response) => response.data);
};

export const deleteHazardTask = async ({ tourOrderId, hazardTaskId }: DeleteHazardTaskPayload) => {
    return await api.delete(`/tourOrders/${tourOrderId}/hazardTask/${hazardTaskId}`).then((response) => response.data);
};

export const updateHazardTask = async ({ tourOrderId, hazardTaskId, ...payload }: UpdateHazardTaskPayload) => {
    return await api
        .put(`/tourOrders/${tourOrderId}/hazardTask/${hazardTaskId}`, payload)
        .then((response) => response.data);
};

export const validateUnloadedSupports = async ({ tourOrderId, ...payload }: ValidateUnloadedSupportsPayload) => {
    return await api
        .post(`/tourOrders/${tourOrderId}/validateUnloadedSupports`, payload)
        .then((response) => response.data);
};

export const sendDegradedMode = async ({ tourOrderId, ...payload }: SendDegradedModePayload) => {
    return await api.post(`/tourOrders/${tourOrderId}/degradedMode`, payload).then((response) => response.data);
};

export type ImportTourOrdersFromCsvResponse = undefined;

export const importTourOrdersFromCsv = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post<ImportTourOrdersFromCsvResponse>("/tourOrders/import", formData).then((response) => response?.data);
};
